import { GetAllStudents, GetAllCohorts, AddCohort, DeleteCohort, SetCohortInStudent } from "../FirebaseQueries";
import { useEffect, useState } from "react";
import { DataGrid, GridActionsCellItem, GridColDef, GridRowModesModel, GridRowModes, GridRowId, GridRowModel, GridRenderCellParams, useGridApiContext, useGridSelector, gridPaginationModelSelector, useGridApiRef, gridRowSelectionManagerSelector, GridRowSelectionModel } from "@mui/x-data-grid";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Icon,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useColorScheme } from '@mui/material/styles';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { blueGrey, grey, red } from "@mui/material/colors";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../AuthProvider";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';

interface Student {
    id: string;
    email: string;
    fullName: string;
    cohortId: string;
}

interface Cohort {
    id: string;
    name: string;
}

const allCohort: Cohort = { id: "all", name: "All Cohorts" };
const blankCohort: Cohort = { id: "", name: "" };

export default function Students() {
    const [students, setStudents] = useState<Student[]>([]);
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [cohortFilter, setCohortFilter] = useState<string>(allCohort.id);
    const [search, setSearch] = useState<string>("");
    const [isEditCohortsDialogOpen, setIsEditCohortsDialogOpen] = useState<boolean>(false);
    const [isDeleteCohortDialogOpen, setIsDeleteCohortDialogOpen] = useState<boolean>(false);
    const [cohortToDelete, setCohortToDelete] = useState<Cohort>(blankCohort);
    const [createCohortName, setCreateCohortName] = useState<string>("");
    const [isCreatingCohort, setIsCreatingCohort] = useState<boolean>(false);
    const [passwordResetSent, setPasswordResetSent] = useState<boolean>(false);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [isBulkEditing, setIsBulkEditing] = useState<boolean>(false);
    const [cohortToBulkSet, setCohortToBulkSet] = useState<string>("");
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
        type: "include",
        ids: new Set()
    });
    const apiRef = useGridApiRef();
    const { resetPassword, GetCurrentRole } = useAuth();
    const { mode } = useColorScheme();

    const columns: GridColDef[] = [
        { field: "fullName", headerName: "Name", flex: 1, renderCell: (params: GridRenderCellParams<any, string>) => <a href={`/app/students/${params.id}`}>{params.value}</a> },
        { field: "email", headerName: "E-mail", flex: 1 },
        {
            field: "cohortId",
            headerName: "Cohort",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: SelectableCohorts(),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            material={{
                                sx: {
                                    color: "primary.main",
                                },
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem icon={<CancelIcon />} label="Cancel" className="textPrimary" onClick={handleCancelClick(id)} color="inherit" />,
                    ];
                }

                return [
                    <GridActionsCellItem icon={<EditIcon />} label="Edit" className="textPrimary" onClick={handleEditClick(id)} color="inherit" />,
                    <GridActionsCellItem icon={<EmailIcon />} label="Password Reset" className="textPrimary" onClick={handleResetPasswordClick(id)} color="inherit" />,
                ];
            },
        },
    ];

    useEffect(() => {
        const GetStudents = async () => {
            try {
                const studentsSnapshot = await GetAllStudents();
                const value: Student[] = [];
                studentsSnapshot.forEach((doc) => {
                    value.push({
                        id: doc.id,
                        email: doc.data().email,
                        fullName: doc.data().fullName,
                        cohortId: doc.data().cohortId,
                    });
                });

                value.sort((a, b) => {
                    if (a.fullName < b.fullName) return -1;
                    if (a.fullName > b.fullName) return 1;
                    return 0;
                });

                setStudents(value);
            } catch (err) {
                // TODO
                console.log(err);
            }
        };

        const GetCohorts = async () => {
            try {
                const snapshot = await GetAllCohorts();
                let newCohorts: Cohort[] = [];
                snapshot.forEach((doc) => {
                    newCohorts.push({
                        id: doc.id,
                        name: doc.data().name,
                    });
                });
                newCohorts.sort((a, b) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

                setCohorts(newCohorts);
            } catch (err) {
                // TODO
                console.log(err);
            }
        };

        GetCohorts();
        GetStudents();
    }, []);

    // ---------- State ---------- //

    function HandleFilterChange(event: SelectChangeEvent) {
        setCohortFilter(event.target.value);
    }

    function AddToCohorts(cohort: Cohort) {
        let newCohorts: Cohort[] = cohorts;
        newCohorts.push(cohort);

        newCohorts.sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
        });
        setCohorts(newCohorts);
    }

    function RemoveFromCohorts(cohortId: string) {
        let newCohorts: Cohort[] = cohorts.filter((cohort: Cohort) => cohort.id !== cohortId);
        if (cohortFilter === cohortId) {
            setCohortFilter(allCohort.id);
        }
        setCohorts([...newCohorts]);
    }

    function SetCohortInLocalStudents(id: string, cohortId: string) {
        let newStudents: Student[] = students;
        for (let i = 0; i < newStudents.length; i++) {
            if (newStudents[i].id === id) {
                newStudents[i].cohortId = cohortId;
                break;
            }
        }
        setStudents(newStudents);
    }

    function HandleCohortToBulkSetChange(event: SelectChangeEvent) {
        setCohortToBulkSet(event.target.value);
    }

    function ClearRowSelectionModel() {
        setRowSelectionModel({
            type: "include",
            ids: new Set()
        });
        setIsBulkEditing(false);
    }

    // ---------- Component Functions ---------- //

    function HandleSearch(event: any) {
        setSearch(event.target.value);
    }

    function RemoveIdFromBulkEdit(id: GridRowId): void {
        let newRows = rowSelectionModel;
        newRows.ids.delete(id);
        setRowSelectionModel({
            type: "include",
            ids: newRows.ids
        });
    }

    const handleResetPasswordClick = (id: GridRowId) => () => {
        const clickedRow = students.find((student) => student.id === id);
        if (clickedRow !== undefined) {
            // TODO - this doesn't check if it has been sent, just that the email exists
            resetPassword(clickedRow.email);
            setPasswordResetSent(true);
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
        // const editedRow = students.find((student) => student.id === id);
        // console.log(editedRow);
        // if (editedRow!.isNew) {
        // 	setStudents(rows.filter((row) => row.id !== id));
        // }
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false };
        SetCohortInLocalStudents(newRow.id, newRow.cohortId);
        SetCohortInStudent(newRow.id, newRow.cohortId);

        return updatedRow;
    };

    function HandleBulkEditDialogClose() {
        setIsBulkEditing(false);
    }

    function HandleEditCohortsDialogClose() {
        setIsEditCohortsDialogOpen(false);
    }

    function OpenEditCohortsDialog() {
        setIsEditCohortsDialogOpen(true);
    }

    function HandleDeleteCohortDialogClose() {
        setIsDeleteCohortDialogOpen(false);
    }

    function OpenDeleteCohortDialog(cohort: Cohort) {
        setCohortToDelete(cohort);
        setIsDeleteCohortDialogOpen(true);
    }

    function UpdateCreateCohortName(event: any) {
        setCreateCohortName(event.target.value);
    }

    async function HandleDeleteCohort() {
        DeleteCohort(cohortToDelete.id);
        RemoveFromCohorts(cohortToDelete.id);
        setIsEditCohortsDialogOpen(false);
        setIsDeleteCohortDialogOpen(false);
    }

    async function CreateCohort() {
        if (createCohortName === undefined || createCohortName === "") return;

        try {
            setIsCreatingCohort(true);
            const docRef = await AddCohort(createCohortName);
            const newCohort: Cohort = { id: docRef.id, name: createCohortName };
            AddToCohorts(newCohort);
            setIsCreatingCohort(false);
            setCreateCohortName("");
        } catch (err) {
            // TODO
            console.log(err);
        }
    }

    function DownloadCSVFile() {
        let headers: string = "Name,E-mail,Cohort\n";

        let rows: string = "";
        FilteredStudents().forEach((student: Student) => {
            rows += `${student.fullName},${student.email},${CohortNameFromId(student.cohortId)}\n`;
        });

        let csvContent = "data:text/csv;charset=utf-8," + headers + rows;

        var encodedUri = encodeURI(csvContent);
        window.open(encodedUri);
    }

    // ---------- Values ---------- //

    function FilteredStudents(): Student[] {
        let returnValue: Student[] = students;

        if (cohortFilter !== "" && cohortFilter !== allCohort.id) {
            returnValue = returnValue.filter((student: Student) => student.cohortId === cohortFilter);
        }

        if (search !== "") {
            returnValue = returnValue.filter((student: Student) => {
                if (student.email !== undefined) {
                    if (student.email.toLowerCase().includes(search.toLowerCase())) {
                        return true;
                    }
                }

                if (student.fullName !== undefined) {
                    if (student.fullName.toLowerCase().includes(search.toLowerCase())) {
                        return true;
                    }
                }

                return false;
            });
        }
        return returnValue;
    }

    function CohortsAndAll(): Cohort[] {
        return [allCohort, ...cohorts];
    }

    function SelectableCohorts(): string[] {
        let array: any[] = [];
        cohorts.forEach((cohort: Cohort) => {
            if (cohort.id !== allCohort.id) array.push({ value: cohort.id, label: cohort.name });
        });
        return array;
    }

    function CohortsWithoutAllCohort() {
        return cohorts.filter((cohort: Cohort) => cohort.id !== allCohort.id);
    }

    function CohortNameFromId(id: string | undefined): string {
        if (id === undefined) return "";

        for (let i = 0; i < cohorts.length; i++) {
            if (cohorts[i].id === id) return cohorts[i].name;
        }
        return "";
    }

    function HandleBulkEditDialogOpen() {
        setIsBulkEditing(true);
    }

    function HandleBulkEditConfirm() {
        const selectedRows: Set<GridRowId> | undefined = rowSelectionModel?.ids;
        if (selectedRows !== undefined && selectedRows.size > 0) {
            selectedRows.forEach((value: GridRowId) => {
                SetCohortInLocalStudents(value.toString(), cohortToBulkSet);
                SetCohortInStudent(value.toString(), cohortToBulkSet);
            })
        }
        setIsBulkEditing(false);
    }

    return (
        <>
            <Stack spacing={2}>
                <Box sx={{ display: "flex", justifyContent: "right", gap: 1 }}>
                    <Button onClick={HandleBulkEditDialogOpen} variant="contained">
                        <Typography>Bulk Edit</Typography>
                        <Typography sx={{ marginLeft: 1, borderRadius: 100, backgroundColor: 'white', color: 'black', minWidth: '21px' }}>
                            {rowSelectionModel?.ids.size}
                        </Typography>
                    </Button>
                    <TextField
                        onChange={HandleSearch}
                        value={search}
                        placeholder="Search"
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Select label="All Cohorts" value={cohortFilter} onChange={HandleFilterChange} sx={{ minWidth: "200px" }}>
                        {CohortsAndAll().map((cohort: Cohort) => {
                            return (
                                <MenuItem value={cohort.id} key={cohort.id}>
                                    {cohort.name}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    <IconButton onClick={OpenEditCohortsDialog} aria-label="edit">
                        <EditIcon fontSize="inherit" />
                    </IconButton>
                    {GetCurrentRole() === "admin" ? (
                        <Button onClick={DownloadCSVFile} variant="contained">
                            Export CSV
                        </Button>
                    ) : (
                        <></>
                    )}
                </Box>
                <Collapse in={passwordResetSent} mountOnEnter unmountOnExit>
                    <Alert
                        icon={<EmailIcon fontSize="inherit" />}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setPasswordResetSent(false);
                                }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        Password reset email sent!
                    </Alert>
                </Collapse>
                <Paper variant="outlined">
                    <DataGrid
                        apiRef={apiRef}
                        checkboxSelection
                        pageSizeOptions={[15, 50]}
                        sx={{ p: 1 }}
                        editMode="row"
                        rows={FilteredStudents()}
                        columns={columns}
                        rowModesModel={rowModesModel}
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel);
                        }}
                        rowSelectionModel={rowSelectionModel}
                        processRowUpdate={processRowUpdate}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 15 } },
                        }}
                    />
                </Paper>
            </Stack>

            {/* ---------- Dialogs and Alerts ---------- */}
            <Dialog open={isEditCohortsDialogOpen} onClose={HandleEditCohortsDialogClose}>
                <DialogTitle>Edit Cohorts</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <DialogContentText>Add and Delete Cohorts.</DialogContentText>
                        <Stack direction={"row"} spacing={1}>
                            <TextField disabled={isCreatingCohort} sx={{ flex: 1 }} value={createCohortName} onChange={UpdateCreateCohortName}></TextField>
                            <Button loading={isCreatingCohort} disabled={isCreatingCohort} variant="contained" onClick={CreateCohort}>
                                Create
                            </Button>
                        </Stack>
                        <Stack spacing={1} sx={{ height: "200px", width: "400px", overflowY: "auto" }}>
                            {CohortsWithoutAllCohort().map((cohort: Cohort) => {
                                return (
                                    <Paper key={cohort.id} variant="outlined">
                                        <Stack direction={"row"} spacing={1} sx={{ pl: 1, justifyContent: "space-between", alignItems: "center" }}>
                                            <Typography>{cohort.name}</Typography>
                                            <IconButton onClick={() => OpenDeleteCohortDialog(cohort)} aria-label="delete">
                                                <DeleteIcon sx={{ color: red[300] }} />
                                            </IconButton>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                        <DialogActions>
                            <Button variant="outlined" onClick={HandleEditCohortsDialogClose}>
                                Close
                            </Button>
                        </DialogActions>
                    </Stack>
                </DialogContent>
            </Dialog>
            {/* ---------- Bulk Edit Dialog ---------- */}
            <Dialog open={isBulkEditing} onClose={HandleBulkEditDialogClose}>
                <DialogTitle>Bulk Edit Students</DialogTitle>
                <DialogContent sx={{ width: '500px' }}>
                    <Stack spacing={4}>
                        <Box>
                            <Stack direction="row" sx={{ width: 1, alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                <Typography component="span">{rowSelectionModel.ids.size} student(s) selected</Typography>
                                <Button onClick={ClearRowSelectionModel} variant="contained">Clear All</Button>
                            </Stack>
                            <Stack
                                spacing={0.5}
                                sx={{
                                    maxHeight: '240px',
                                    overflowY: "scroll",
                                    padding: 1,
                                    border: "1px solid",
                                    borderRadius: 1,
                                    borderColor: mode === "light" ? blueGrey[300] : blueGrey[700]
                                }}>
                                {Array.from(rowSelectionModel.ids).map((rowId: GridRowId) => {
                                    const student: Student | undefined = students.find((value: Student) => value.id === rowId);
                                    return (
                                        <Card key={rowId} sx={{ padding: 1, minHeight: "36px" }}>
                                            <Stack direction={"row"} sx={{ justifyContent: 'space-between' }}>
                                                <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
                                                    <Typography>{student?.fullName}</Typography>
                                                    <Typography>({student?.email})</Typography>
                                                </Stack>
                                                <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
                                                    <Typography>{CohortNameFromId(student?.cohortId)}</Typography>
                                                    <IconButton onClick={() => RemoveIdFromBulkEdit(rowId)} size="small">
                                                        <ClearIcon />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </Card>
                                    )
                                })}
                            </Stack>
                        </Box>
                        <Stack spacing={1}>
                            <Typography>Set selected students to the same cohort</Typography>
                            <Select label="Cohorts" value={cohortToBulkSet} onChange={HandleCohortToBulkSetChange} sx={{ minWidth: "200px" }}>
                                {cohorts.map((cohort: Cohort) => {
                                    return (
                                        <MenuItem value={cohort.id} key={cohort.id}>
                                            {cohort.name}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={HandleBulkEditDialogClose}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={HandleBulkEditConfirm}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeleteCohortDialogOpen} onClose={HandleDeleteCohortDialogClose}>
                <DialogTitle>Delete Cohort: {cohortToDelete.name}</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure? This will also remove the cohort from any student that has it assigned.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={HandleDeleteCohortDialogClose} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={HandleDeleteCohort} color="error" variant="contained">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
